import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary'
import type { FallbackProps } from 'react-error-boundary'
import type { ComponentType, ReactNode } from 'react'
import { Box, Typography } from '@mui/material'
import Button from './Button'

const DefaultFallback = ({ error, resetErrorBoundary }: FallbackProps) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '40vh',
            gap: 2,
            p: 4,
            textAlign: 'center',
        }}
    >
        <Typography sx={{ fontWeight: 'bold', fontSize: '16px' }}>
            Something went wrong
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '12px' }}>
            {error instanceof Error ? error.message : String(error)}
        </Typography>
        <Button onClick={resetErrorBoundary} variant="outlined">
            Try again
        </Button>
    </Box>
)

interface ErrorBoundaryProps {
    children: ReactNode
    /** Override the default error UI with a custom fallback component */
    FallbackComponent?: ComponentType<FallbackProps>
}

/**
 * Catches render errors and shows a fallback UI.
 * Wrap route-level components to prevent the whole app from crashing.
 *
 * @example
 * <ErrorBoundary>
 *   <SomeFeatureComponent />
 * </ErrorBoundary>
 */
const ErrorBoundary = ({ children, FallbackComponent = DefaultFallback }: ErrorBoundaryProps) => (
    <ReactErrorBoundary FallbackComponent={FallbackComponent}>
        {children}
    </ReactErrorBoundary>
)

export default ErrorBoundary
export type { FallbackProps }
